import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useData } from "../../DataContext";
import { apiRequest } from "../../api/http";
import { generateId } from "../../utils/id";

import { Card } from "../../components/ui/Card";
import { TableShell, CellClamp } from "../../components/ui/TableShell";

import {
  DECISION_STATUSES,
  DecisionStatus,
  findTopicDef,
  TOPICS,
  topicGroups,
  TOPIC_PACKS,
  packTopics,
} from "./decisionTopics";

type Props = {
  projectId: string;
  onChanged?: () => void | Promise<void>;
};

type ApiArea = {
  id: string;
  tenant_id: string;
  project_id: string;
  name: string;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
};

type ApiDecision = {
  id: string;
  tenant_id: string;
  project_id: string;
  area_id: string | null;

  topic: string;
  selection: string;
  status: string;

  impact_plumbing: number; // 0/1
  impact_electrical: number; // 0/1
  impact_layout: number; // 0/1
  impact_notes: string | null;

  location_notes: string | null;
  notes: string | null;

  created_at?: string;
  updated_at?: string;
};

type UiArea = { id: string; name: string; sortOrder: number };
type UiDecision = {
  id: string;
  areaId: string | null;
  topic: string;
  selection: string;
  status: DecisionStatus;
  impactPlumbing: boolean;
  impactElectrical: boolean;
  impactLayout: boolean;
  impactNotes: string;
  locationNotes: string;
  notes: string;
  updatedAt?: string;
};

type ApiOk<T> = { ok: true; data: T };
type ApiErr = { ok: false; error: string };

const pill = (active: boolean) => `btn btnSm ${active ? "" : "btnSecondary"}`;

function toUiArea(a: ApiArea): UiArea {
  return { id: a.id, name: a.name, sortOrder: Number(a.sort_order) || 0 };
}

function toUiDecision(d: ApiDecision): UiDecision {
  const statusRaw = (d.status || "draft").toLowerCase().trim();
  const status: DecisionStatus =
    statusRaw === "confirmed" || statusRaw === "ordered" || statusRaw === "installed"
      ? (statusRaw as any)
      : "draft";

  return {
    id: d.id,
    areaId: d.area_id ?? null,
    topic: d.topic || "",
    selection: d.selection || "",
    status,
    impactPlumbing: Number(d.impact_plumbing) === 1,
    impactElectrical: Number(d.impact_electrical) === 1,
    impactLayout: Number(d.impact_layout) === 1,
    impactNotes: d.impact_notes || "",
    locationNotes: d.location_notes || "",
    notes: d.notes || "",
    updatedAt: d.updated_at,
  };
}

function impactsText(d: UiDecision) {
  const out: string[] = [];
  if (d.impactPlumbing) out.push("🔧");
  if (d.impactElectrical) out.push("⚡");
  if (d.impactLayout) out.push("📐");
  return out.join(" ");
}

function escapeHtml(s: string) {
  return (s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/**
 * Topic Packs (UI only)
 * We store packs in the <select> as "__PACK__:<packKey>"
 * and resolve via packTopics(packKey).
 */
const PACK_PREFIX = "__PACK__:";
function isPackValue(v: string) {
  return (v || "").startsWith(PACK_PREFIX);
}
function packKeyFromValue(v: string) {
  return (v || "").slice(PACK_PREFIX.length).trim();
}

export default function ClientDecisionsTab({ projectId, onChanged }: Props) {
  const { t } = useTranslation();
  const { tenantId } = useData() as any;

  const [viewMode, setViewMode] = useState<"board" | "table">("board");

  // Collapsibles (collapsed by default)
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  // Filters
  const [areaFilter, setAreaFilter] = useState<string>("__ALL__");
  const [statusFilter, setStatusFilter] = useState<string>("__ALL__");
  const [impactFilter, setImpactFilter] = useState<string>("__ANY__"); // ANY|P|E|L
  const [search, setSearch] = useState("");

  // Data
  const [areas, setAreas] = useState<UiArea[]>([]);
  const [decisions, setDecisions] = useState<UiDecision[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Inline Add Area
  const [addAreaOpen, setAddAreaOpen] = useState(false);
  const [newAreaName, setNewAreaName] = useState("");

  // Add decision
  const [addAreaId, setAddAreaId] = useState<string>(""); // "" => Whole Project
  const [addTopic, setAddTopic] = useState<string>(TOPICS[0]?.value || "Other");
  const [addSelection, setAddSelection] = useState<string>("");
  const [addStatus, setAddStatus] = useState<DecisionStatus>("draft");
  const [addImpactP, setAddImpactP] = useState(false);
  const [addImpactE, setAddImpactE] = useState(false);
  const [addImpactL, setAddImpactL] = useState(false);
  const [addImpactNotes, setAddImpactNotes] = useState("");
  const [addLocationNotes, setAddLocationNotes] = useState("");
  const [addNotes, setAddNotes] = useState("");

  // Edit decision (inline — board and table share state)
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editAreaId, setEditAreaId] = useState<string>("");
  const [editTopic, setEditTopic] = useState<string>("");
  const [editSelection, setEditSelection] = useState<string>("");
  const [editStatus, setEditStatus] = useState<DecisionStatus>("draft");
  const [editImpactP, setEditImpactP] = useState(false);
  const [editImpactE, setEditImpactE] = useState(false);
  const [editImpactL, setEditImpactL] = useState(false);
  const [editImpactNotes, setEditImpactNotes] = useState("");
  const [editLocationNotes, setEditLocationNotes] = useState("");
  const [editNotes, setEditNotes] = useState("");

  const loadAll = async () => {
    setLoading(true);
    setErr(null);
    try {
      const [areasRes, decRes] = await Promise.all([
        apiRequest<ApiOk<ApiArea[]> | ApiErr>(
          "GET",
          `/api/project_areas?project_id=${encodeURIComponent(projectId)}`,
          { tenantId }
        ),
        apiRequest<ApiOk<ApiDecision[]> | ApiErr>(
          "GET",
          `/api/project_decisions?project_id=${encodeURIComponent(projectId)}`,
          { tenantId }
        ),
      ]);

      if (!(areasRes as any).ok) {
        setErr((areasRes as any).error || "Failed to load areas.");
        setAreas([]);
      } else {
        setAreas((((areasRes as any).data || []) as ApiArea[]).map(toUiArea));
      }

      if (!(decRes as any).ok) {
        setErr((prev) => prev || ((decRes as any).error || "Failed to load decisions."));
        setDecisions([]);
      } else {
        setDecisions((((decRes as any).data || []) as ApiDecision[]).map(toUiDecision));
      }
    } catch (e: any) {
      setErr(e?.message || "Failed to load client decisions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, projectId]);

  const areaNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const a of areas) map[a.id] = a.name;
    return map;
  }, [areas]);

  const stats = useMemo(() => {
    const p = decisions.filter((d) => d.impactPlumbing).length;
    const e = decisions.filter((d) => d.impactElectrical).length;
    const l = decisions.filter((d) => d.impactLayout).length;
    const unconfirmed = decisions.filter((d) => d.status === "draft").length;
    return { p, e, l, unconfirmed };
  }, [decisions]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return decisions.filter((d) => {
      if (areaFilter !== "__ALL__") {
        const a = d.areaId || "__WHOLE__";
        if (a !== areaFilter) return false;
      }
      if (statusFilter !== "__ALL__") {
        if (d.status !== statusFilter) return false;
      }
      if (impactFilter !== "__ANY__") {
        if (impactFilter === "P" && !d.impactPlumbing) return false;
        if (impactFilter === "E" && !d.impactElectrical) return false;
        if (impactFilter === "L" && !d.impactLayout) return false;
      }
      if (!q) return true;
      const hay = `${d.topic} ${d.selection} ${d.notes} ${d.locationNotes} ${d.impactNotes}`.toLowerCase();
      return hay.includes(q);
    });
  }, [decisions, areaFilter, statusFilter, impactFilter, search]);

  const groupedForBoard = useMemo(() => {
    const buckets: Record<string, UiDecision[]> = {};
    for (const d of filtered) {
      const key = d.areaId || "__WHOLE__";
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push(d);
    }

    const keys: string[] = [];

    if (areaFilter !== "__ALL__") {
      keys.push(areaFilter);
      return { keys, buckets };
    }

    const wholeHas = (buckets["__WHOLE__"] || []).length > 0;
    for (const a of areas) {
      if ((buckets[a.id] || []).length > 0) keys.push(a.id);
    }
    if (wholeHas) keys.unshift("__WHOLE__");
    return { keys, buckets };
  }, [filtered, areas, areaFilter]);

  const applyTopicDefaultsToAdd = (topic: string) => {
    if (isPackValue(topic)) return; // packs don't have a single default impact
    const def = findTopicDef(topic);
    const d = def?.defaultImpacts || {};
    setAddImpactP(Boolean(d.plumbing));
    setAddImpactE(Boolean(d.electrical));
    setAddImpactL(Boolean(d.layout));
  };

  const applyTopicDefaultsToEdit = (topic: string) => {
    const def = findTopicDef(topic);
    const d = def?.defaultImpacts || {};
    setEditImpactP(Boolean(d.plumbing));
    setEditImpactE(Boolean(d.electrical));
    setEditImpactL(Boolean(d.layout));
  };

  const addArea = async () => {
    const name = newAreaName.trim();
    if (!name) {
      alert(t("clientDecisions.areaRequired"));
      return;
    }

    const id = generateId("AREA");
    try {
      const res = await apiRequest<ApiOk<ApiArea> | ApiErr>("POST", "/api/project_areas", {
        tenantId,
        body: { id, project_id: projectId, name, sort_order: areas.length * 10 },
      });

      if (!(res as any).ok) {
        alert((res as any).error || t("clientDecisions.failAddArea"));
        return;
      }

      setAreas((prev) => [...prev, toUiArea((res as any).data as ApiArea)]);
      setNewAreaName("");
      setAddAreaOpen(false);
    } catch (e: any) {
      alert(e?.message || t("clientDecisions.failAddArea"));
    }
  };

  const deleteArea = async (areaId: string) => {
    const name = areaNameById[areaId] || areaId;
    const ok = window.confirm(t("clientDecisions.confirmDeleteArea", { name }));
    if (!ok) return;

    try {
      const res = await apiRequest<ApiOk<any> | ApiErr>(
        "DELETE",
        `/api/project_areas/${encodeURIComponent(areaId)}`,
        { tenantId }
      );

      if (!(res as any).ok) {
        alert((res as any).error || t("clientDecisions.failDeleteArea"));
        return;
      }

      setAreas((prev) => prev.filter((a) => a.id !== areaId));
      await loadAll();
    } catch (e: any) {
      alert(e?.message || t("clientDecisions.failDeleteArea"));
    }
  };

  const addSingleDecision = async (payload: {
    area_id: string | null;
    topic: string;
    selection: string;
    status: DecisionStatus;
    impact_plumbing: number;
    impact_electrical: number;
    impact_layout: number;
    impact_notes: string | null;
    location_notes: string | null;
    notes: string | null;
  }) => {
    const id = generateId("DEC");
    const res = await apiRequest<ApiOk<ApiDecision> | ApiErr>("POST", "/api/project_decisions", {
      tenantId,
      body: {
        id,
        project_id: projectId,
        ...payload,
      },
    });
    return res;
  };

  const addDecision = async () => {
    const topicRaw = (addTopic || "").trim();
    const area_id = addAreaId.trim() || null;

    if (!topicRaw) return alert(t("clientDecisions.topicRequired"));

    // ✅ PACK BEHAVIOR (NOW DRIVEN BY decisionTopics.ts TOPIC_PACKS)
    if (isPackValue(topicRaw)) {
      const key = packKeyFromValue(topicRaw);
      const topics = packTopics(key);
      const label = TOPIC_PACKS.find((p) => p.key === key)?.label || "Topic Pack";

      if (!topics.length) {
        alert(`This pack has no topics (pack="${key}"). Check TOPIC_PACKS/packTopics.`);
        return;
      }

      const ok = window.confirm(t("clientDecisions.confirmPackAdd", { count: topics.length, label }));
      if (!ok) return;

      try {
        const createdUi: UiDecision[] = [];

        for (const t of topics) {
          const def = findTopicDef(t);
          const di = def?.defaultImpacts || {};
          const res = await addSingleDecision({
            area_id,
            topic: t,
            selection: "TBD",
            status: addStatus,
            impact_plumbing: di.plumbing ? 1 : 0,
            impact_electrical: di.electrical ? 1 : 0,
            impact_layout: di.layout ? 1 : 0,
            impact_notes: null,
            location_notes: null,
            notes: `Auto-added from pack: ${label}`,
          });

          if (!(res as any).ok) {
            alert((res as any).error || `Failed while adding: ${t}`);
            break;
          }

          createdUi.push(toUiDecision((res as any).data as ApiDecision));
        }

        if (createdUi.length) {
          setDecisions((prev) => [...createdUi.reverse(), ...prev]);
          await onChanged?.();
        }

        // reset (keep area + pack selection)
        setAddSelection("");
        setAddImpactNotes("");
        setAddLocationNotes("");
        setAddNotes("");
      } catch (e: any) {
        alert(e?.message || t("clientDecisions.failAddDecision"));
      }

      return;
    }

    // ✅ NORMAL SINGLE DECISION
    const selection = (addSelection || "").trim();
    if (!selection) return alert(t("clientDecisions.selectionRequired"));

    try {
      const res = await addSingleDecision({
        area_id,
        topic: topicRaw,
        selection,
        status: addStatus,
        impact_plumbing: addImpactP ? 1 : 0,
        impact_electrical: addImpactE ? 1 : 0,
        impact_layout: addImpactL ? 1 : 0,
        impact_notes: addImpactNotes.trim() || null,
        location_notes: addLocationNotes.trim() || null,
        notes: addNotes.trim() || null,
      });

      if (!(res as any).ok) {
        alert((res as any).error || t("clientDecisions.failAddDecision"));
        return;
      }

      const created = toUiDecision((res as any).data as ApiDecision);
      setDecisions((prev) => [created, ...prev]);

      await onChanged?.();

      // reset add form (keep area + topic)
      setAddSelection("");
      setAddStatus("draft");
      applyTopicDefaultsToAdd(addTopic);
      setAddImpactNotes("");
      setAddLocationNotes("");
      setAddNotes("");
    } catch (e: any) {
      alert(e?.message || t("clientDecisions.failAddDecision"));
    }
  };

  const startEdit = (d: UiDecision) => {
    setEditingId(d.id);
    setEditAreaId(d.areaId || "");
    setEditTopic(d.topic);
    setEditSelection(d.selection);
    setEditStatus(d.status);
    setEditImpactP(d.impactPlumbing);
    setEditImpactE(d.impactElectrical);
    setEditImpactL(d.impactLayout);
    setEditImpactNotes(d.impactNotes);
    setEditLocationNotes(d.locationNotes);
    setEditNotes(d.notes);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditAreaId("");
    setEditTopic("");
    setEditSelection("");
    setEditStatus("draft");
    setEditImpactP(false);
    setEditImpactE(false);
    setEditImpactL(false);
    setEditImpactNotes("");
    setEditLocationNotes("");
    setEditNotes("");
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const topic = (editTopic || "").trim();
    const selection = (editSelection || "").trim();
    if (!topic) return alert(t("clientDecisions.topicRequired"));
    if (!selection) return alert(t("clientDecisions.selectionRequired"));

    try {
      const res = await apiRequest<ApiOk<ApiDecision> | ApiErr>(
        "PATCH",
        `/api/project_decisions/${encodeURIComponent(editingId)}`,
        {
          tenantId,
          body: {
            area_id: editAreaId.trim() || null,
            topic,
            selection,
            status: editStatus,
            impact_plumbing: editImpactP ? 1 : 0,
            impact_electrical: editImpactE ? 1 : 0,
            impact_layout: editImpactL ? 1 : 0,
            impact_notes: editImpactNotes.trim() || null,
            location_notes: editLocationNotes.trim() || null,
            notes: editNotes.trim() || null,
          },
        }
      );

      if (!(res as any).ok) {
        alert((res as any).error || t("clientDecisions.failUpdateDecision"));
        return;
      }

      const updated = toUiDecision((res as any).data as ApiDecision);
      setDecisions((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));

      await onChanged?.();
      cancelEdit();
    } catch (e: any) {
      alert(e?.message || t("clientDecisions.failUpdateDecision"));
    }
  };

  const doDelete = async (d: UiDecision) => {
    const ok = window.confirm(t("clientDecisions.confirmDeleteDecision", { topic: d.topic, selection: d.selection }));
    if (!ok) return;

    try {
      const res = await apiRequest<ApiOk<any> | ApiErr>(
        "DELETE",
        `/api/project_decisions/${encodeURIComponent(d.id)}`,
        { tenantId }
      );

      if (!(res as any).ok) {
        alert((res as any).error || t("clientDecisions.failDeleteDecision"));
        return;
      }

      setDecisions((prev) => prev.filter((x) => x.id !== d.id));
      await onChanged?.();

      if (editingId === d.id) cancelEdit();
    } catch (e: any) {
      alert(e?.message || t("clientDecisions.failDeleteDecision"));
    }
  };

  const topicGroupsList = useMemo(() => topicGroups(), []);

  // -----------------------------
  // Printing (NO POPUPS)
  // -----------------------------
  const printHtml = (title: string, bodyHtml: string) => {
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.setAttribute("aria-hidden", "true");

    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document;
    if (!doc || !iframe.contentWindow) {
      document.body.removeChild(iframe);
      alert(t("clientDecisions.printUnableSurface"));
      return;
    }

    const full = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>
    body { font-family: Arial, sans-serif; padding: 18px; color: #111; }
    h1 { font-size: 18px; margin: 0 0 10px 0; }
    .meta { font-size: 12px; color: #444; margin-bottom: 14px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #ddd; padding: 8px; vertical-align: top; }
    th { background: #f3f3f3; text-align: left; }
    .pill { display:inline-block; padding: 2px 8px; border:1px solid #ddd; border-radius:999px; font-size: 12px; background:#fafafa; }
    .muted { color:#666; font-size:12px; }
    .nowrap { white-space: nowrap; }
  </style>
</head>
<body>
  ${bodyHtml}
</body>
</html>`;

    doc.open();
    doc.write(full);
    doc.close();

    setTimeout(() => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } finally {
        setTimeout(() => {
          try {
            document.body.removeChild(iframe);
          } catch {}
        }, 500);
      }
    }, 50);
  };

  const printArea = (areaKey: string) => {
    const areaName = areaKey === "__WHOLE__" ? "Whole Project" : areaNameById[areaKey] || "Area";
    const list = (groupedForBoard.buckets[areaKey] || []).slice();

    const htmlRows = list
      .map((d) => {
        const impacts = impactsText(d) || "—";
        const notesLine =
          d.locationNotes || d.impactNotes
            ? `<div class="muted">${
                d.locationNotes ? `Location: ${escapeHtml(d.locationNotes)}` : ""
              }${d.locationNotes && d.impactNotes ? " • " : ""}${
                d.impactNotes ? `Impact: ${escapeHtml(d.impactNotes)}` : ""
              }</div>`
            : "";

        return `<tr>
          <td style="width:180px;"><div><b>${escapeHtml(d.topic)}</b></div><div class="muted">${escapeHtml(
          d.status
        )}</div></td>
          <td>${escapeHtml(d.selection)}${notesLine}</td>
          <td class="nowrap" style="width:110px;">${escapeHtml(impacts)}</td>
          <td style="width:220px;">${escapeHtml(d.notes || "")}</td>
        </tr>`;
      })
      .join("");

    const body = `
      <h1>Client Decisions — ${escapeHtml(areaName)}</h1>
      <div class="meta">${list.length} item${list.length === 1 ? "" : "s"}</div>
      <table>
        <thead>
          <tr>
            <th>${t("clientDecisions.printColTopicStatus")}</th>
            <th>${t("clientDecisions.printColSelection")}</th>
            <th>${t("clientDecisions.printColImpacts")}</th>
            <th>${t("clientDecisions.printColNotes")}</th>
          </tr>
        </thead>
        <tbody>
          ${htmlRows || `<tr><td colspan="4" class="muted">${t("clientDecisions.printNoDecisions")}</td></tr>`}
        </tbody>
      </table>
    `;

    printHtml(`Client Decisions - ${areaName}`, body);
  };

  const printTable = () => {
    const htmlRows = filtered
      .map((d) => {
        const areaName = d.areaId ? areaNameById[d.areaId] || "Area" : "Whole Project";
        const impacts = impactsText(d) || "—";
        const detail =
          d.locationNotes || d.impactNotes
            ? `<div class="muted">${
                d.locationNotes ? `Location: ${escapeHtml(d.locationNotes)}` : ""
              }${d.locationNotes && d.impactNotes ? " • " : ""}${
                d.impactNotes ? `Impact: ${escapeHtml(d.impactNotes)}` : ""
              }</div>`
            : "";
        return `<tr>
          <td style="width:170px;">${escapeHtml(areaName)}</td>
          <td style="width:220px;"><b>${escapeHtml(d.topic)}</b></td>
          <td>${escapeHtml(d.selection)}${detail}</td>
          <td style="width:120px;"><span class="pill">${escapeHtml(d.status)}</span></td>
          <td style="width:90px;" class="nowrap">${escapeHtml(impacts)}</td>
        </tr>`;
      })
      .join("");

    const body = `
      <h1>${t("clientDecisions.printDecisionLog")}</h1>
      <div class="meta">${filtered.length} item${filtered.length === 1 ? "" : "s"} (current filters)</div>
      <table>
        <thead>
          <tr>
            <th>${t("clientDecisions.printColArea")}</th>
            <th>${t("clientDecisions.printColTopic")}</th>
            <th>${t("clientDecisions.printColSelection")}</th>
            <th>${t("clientDecisions.printColStatus")}</th>
            <th>${t("clientDecisions.printColImpacts")}</th>
          </tr>
        </thead>
        <tbody>
          ${htmlRows || `<tr><td colspan="5" class="muted">${t("clientDecisions.printNoDecisionsAll")}</td></tr>`}
        </tbody>
      </table>
    `;

    printHtml("Client Decisions - Decision Log", body);
  };

  return (
    <div className="container" style={{ padding: 0 }}>
      <style>{`
        .cdTopRow { display:flex; justify-content:space-between; align-items:flex-end; gap:12px; flex-wrap:wrap; }
        .cdSectionHdr { display:flex; justify-content:space-between; align-items:center; gap:10px; flex-wrap:wrap; }
        .cdSectionTitle { font-weight: 1000; letter-spacing: -0.2px; display:flex; align-items:center; gap:10px; min-width: 0; }
        .cdChevron { font-size: 14px; opacity: 0.8; }

        .cdLabel { color: var(--muted); font-weight: 900; font-size: 12px; }
        .cdInput {
          width: 100%;
          min-width:0;
          padding: 10px 12px;
          border-radius: 12px;
          border: 1px solid rgba(15,23,42,0.12);
          background: rgba(255,255,255,0.85);
          outline: none;
          box-sizing: border-box;
        }
        .cdInput:focus { border-color: rgba(15,23,42,0.22); box-shadow: 0 0 0 3px rgba(15,23,42,0.08); }

        .cdFiltersGrid {
          display:grid;
          gap: 12px;
          grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
          align-items:end;
          margin-top: 12px;
        }
        .cdField { display:flex; flex-direction:column; gap:6px; min-width:0; }

        .cdInlineAreaBox {
          margin-top: 10px;
          padding: 10px;
          border-radius: 14px;
          border: 1px solid rgba(15,23,42,0.10);
          background: rgba(255,255,255,0.70);
        }
        .cdInlineAreaRow { display:flex; gap:10px; align-items:flex-end; flex-wrap:wrap; }
        .cdInlineAreaRow > * { min-width: 0; }

        .cdStats {
          display:grid;
          gap:10px;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          margin-top: 12px;
        }
        .cdStat {
          display:flex; justify-content:space-between; align-items:baseline;
          padding: 8px 10px; border-radius: 14px;
          border: 1px solid rgba(15,23,42,0.08);
          background: rgba(255,255,255,0.70);
          min-width: 0;
        }
        .cdStatLabel { color: var(--muted); font-weight: 900; font-size: 12px; }
        .cdStatValue { font-weight: 1000; letter-spacing: -0.2px; }

        .cdAddGrid {
          display: grid;
          gap: 12px;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          align-items: end;
          min-width: 0;
        }
        @media (max-width: 560px) { .cdAddGrid { grid-template-columns: 1fr; } }

        .cdChecks { display:flex; gap:10px; flex-wrap:wrap; }
        .cdCheck { display:flex; gap:8px; align-items:center; font-weight: 900; color: var(--text); }
        .cdCheck input { transform: translateY(1px); }

        .cdBoardGrid {
          margin-top: 14px;
          display:grid;
          gap: 12px;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          align-items:start;
        }
        @media (max-width: 980px) { .cdBoardGrid { grid-template-columns: 1fr; } }

        .cdAreaCard {
          border: 1px solid rgba(15,23,42,0.10);
          border-radius: 16px;
          background: rgba(255,255,255,0.72);
          padding: 12px;
          min-width: 0;
        }
        .cdAreaHdr { display:flex; justify-content:space-between; align-items:flex-start; gap:10px; margin-bottom: 8px; flex-wrap: wrap; }
        .cdAreaTitle { font-weight: 1000; letter-spacing: -0.2px; display:flex; gap:10px; align-items:center; flex-wrap:wrap; min-width:0; }
        .cdAreaMeta { color: var(--muted); font-weight: 900; font-size: 12px; display:flex; gap:10px; flex-wrap:wrap; justify-content:flex-end; }
        .cdList { display:flex; flex-direction:column; gap:8px; }

        .cdItem {
          padding: 10px 10px;
          border-radius: 14px;
          border: 1px solid rgba(15,23,42,0.08);
          background: rgba(255,255,255,0.78);
          display:flex;
          justify-content:space-between;
          gap:10px;
          align-items:flex-start;
          min-width:0;
          flex-wrap: wrap;
        }

        /* ✅ KEY FIX: when editing, force column layout so the edit form uses FULL width */
        .cdItem.editing { flex-direction: column; }
        .cdItem.editing .cdActions { display: none; }
        .cdItem.editing .cdItemLeft { width: 100%; }

        .cdItemLeft { min-width: 0; flex: 1; width: 100%; }
        .cdItemTop { display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
        .cdTopic { font-weight: 1000; }
        .cdStatus {
          font-size: 12px;
          font-weight: 1000;
          padding: 4px 8px;
          border-radius: 999px;
          border: 1px solid rgba(15,23,42,0.10);
          background: rgba(255,255,255,0.65);
        }
        .cdSel { margin-top: 4px; font-weight: 900; color: var(--text); }
        .cdMuted { color: var(--muted); font-weight: 800; font-size: 12px; margin-top: 6px; }
        .cdActions { display:flex; gap:8px; flex-wrap:wrap; justify-content:flex-end; }

        .cdInlineEditWrap {
          padding: 10px;
          border-radius: 14px;
          border: 1px solid rgba(15,23,42,0.10);
          background: rgba(255,255,255,0.70);
          margin-top: 10px;
          min-width: 0;
          width: 100%;
          box-sizing: border-box;
        }

        .cdTableWrap { width: 100%; overflow-x: auto; }
        .cdTableWrap table { min-width: 920px; }
      `}</style>

      <Card
        title={
          <div className="cdTopRow">
            <div style={{ fontWeight: 1000, letterSpacing: "-0.2px" }}>{t("clientDecisions.title")}</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <button className={pill(viewMode === "board")} type="button" onClick={() => setViewMode("board")}>
                {t("clientDecisions.boardBtn")}
              </button>
              <button className={pill(viewMode === "table")} type="button" onClick={() => setViewMode("table")}>
                {t("clientDecisions.tableBtn")}
              </button>
            </div>
          </div>
        }
        subtitle={t("clientDecisions.subtitle")}
      >
        {err ? <div style={{ color: "var(--bad)", fontWeight: 800, marginBottom: 10 }}>{err}</div> : null}

        {/* Filters */}
        <div style={{ marginTop: 8 }}>
          <div className="cdSectionHdr">
            <div className="cdSectionTitle">
              <span className="cdChevron">{filtersOpen ? "▾" : "▸"}</span>
              {t("clientDecisions.filtersLabel")}
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <button className="btn btnSm btnSecondary" type="button" onClick={() => setFiltersOpen((v) => !v)}>
                {filtersOpen ? t("clientDecisions.collapse") : t("clientDecisions.expand")}
              </button>

              <button
                className="btn btnSm btnSecondary"
                type="button"
                onClick={() => setAddAreaOpen((v) => !v)}
                title={t("clientDecisions.addAreaInlineTip")}
              >
                {addAreaOpen ? t("clientDecisions.closeAddArea") : t("clientDecisions.addAreaBtn")}
              </button>

              {areas.length ? (
                <button
                  className="btn btnSm btnSecondary"
                  type="button"
                  onClick={() => {
                    const pick = prompt(`Type exact area name to delete:\n\n${areas.map((a) => `• ${a.name}`).join("\n")}`);
                    const hit = areas.find((a) => a.name.toLowerCase() === (pick || "").trim().toLowerCase());
                    if (!hit) return;
                    deleteArea(hit.id);
                  }}
                  title={t("clientDecisions.deleteAreaTip")}
                >
                  {t("clientDecisions.deleteAreaBtn")}
                </button>
              ) : null}
            </div>
          </div>

          {addAreaOpen ? (
            <div className="cdInlineAreaBox">
              <div style={{ fontWeight: 1000, marginBottom: 8 }}>{t("clientDecisions.addAreaTitle")}</div>
              <div className="cdInlineAreaRow">
                <div className="cdField" style={{ flex: "1 1 320px" }}>
                  <div className="cdLabel">{t("clientDecisions.areaNameLabel")}</div>
                  <input
                    className="cdInput"
                    value={newAreaName}
                    onChange={(e) => setNewAreaName(e.target.value)}
                    placeholder={t("clientDecisions.areaNamePlaceholder")}
                  />
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button className="btn btnSm" type="button" onClick={addArea}>
                    {t("clientDecisions.saveBtn")}
                  </button>
                  <button className="btn btnSecondary btnSm" type="button" onClick={() => setAddAreaOpen(false)}>
                    {t("clientDecisions.cancelBtn")}
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {filtersOpen ? (
            <>
              <div className="cdFiltersGrid">
                <div className="cdField">
                  <div className="cdLabel">{t("clientDecisions.areaFilterLabel")}</div>
                  <select className="cdInput" value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)}>
                    <option value="__ALL__">{t("clientDecisions.allAreas")}</option>
                    <option value="__WHOLE__">{t("clientDecisions.wholeProject")}</option>
                    {areas.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="cdField">
                  <div className="cdLabel">{t("clientDecisions.statusFilterLabel")}</div>
                  <select className="cdInput" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="__ALL__">{t("clientDecisions.allStatuses")}</option>
                    {DECISION_STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="cdField">
                  <div className="cdLabel">{t("clientDecisions.impactFilterLabel")}</div>
                  <select className="cdInput" value={impactFilter} onChange={(e) => setImpactFilter(e.target.value)}>
                    <option value="__ANY__">{t("clientDecisions.anyImpact")}</option>
                    <option value="P">🔧 {t("clientDecisions.plumbingImpact")}</option>
                    <option value="E">⚡ {t("clientDecisions.electricalImpact")}</option>
                    <option value="L">📐 {t("clientDecisions.layoutImpact")}</option>
                  </select>
                </div>

                <div className="cdField">
                  <div className="cdLabel">{t("clientDecisions.searchLabel")}</div>
                  <input
                    className="cdInput"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t("clientDecisions.searchPlaceholder")}
                  />
                </div>
              </div>

              <div className="cdStats">
                <div className="cdStat">
                  <div className="cdStatLabel">{t("clientDecisions.plumbingStats")}</div>
                  <div className="cdStatValue">{stats.p}</div>
                </div>
                <div className="cdStat">
                  <div className="cdStatLabel">{t("clientDecisions.electricalStats")}</div>
                  <div className="cdStatValue">{stats.e}</div>
                </div>
                <div className="cdStat">
                  <div className="cdStatLabel">{t("clientDecisions.layoutStats")}</div>
                  <div className="cdStatValue">{stats.l}</div>
                </div>
                <div className="cdStat">
                  <div className="cdStatLabel">{t("clientDecisions.unconfirmedStats")}</div>
                  <div className="cdStatValue">{stats.unconfirmed}</div>
                </div>
              </div>
            </>
          ) : (
            <div style={{ marginTop: 10, color: "var(--muted)", fontWeight: 900, fontSize: 12 }}>
              {t("clientDecisions.collapsedNote")}
            </div>
          )}
        </div>

        {/* Add Decision */}
        <div style={{ marginTop: 14 }}>
          <div className="cdSectionHdr">
            <div className="cdSectionTitle">
              <span className="cdChevron">{addOpen ? "▾" : "▸"}</span>
              {t("clientDecisions.addDecisionTitle")}
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <button className="btn btnSm btnSecondary" type="button" onClick={() => setAddOpen((v) => !v)}>
                {addOpen ? t("clientDecisions.collapse") : t("clientDecisions.expand")}
              </button>
              <button className="btn btnSm" type="button" onClick={addDecision} disabled={!addOpen}>
                {t("clientDecisions.addBtn")}
              </button>
            </div>
          </div>

          {addOpen ? (
            <div style={{ marginTop: 10 }}>
              <TableShell title="" subtitle={t("clientDecisions.addSubtitle")}>
                <div className="cdAddGrid">
                  <div className="cdField">
                    <div className="cdLabel">{t("clientDecisions.areaLabel")}</div>
                    <select className="cdInput" value={addAreaId} onChange={(e) => setAddAreaId(e.target.value)}>
                      <option value="">{t("clientDecisions.wholeProject")}</option>
                      {areas.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="cdField">
                    <div className="cdLabel">{t("clientDecisions.topicLabel")}</div>
                    <select
                      className="cdInput"
                      value={addTopic}
                      onChange={(e) => {
                        const v = e.target.value;
                        setAddTopic(v);
                        applyTopicDefaultsToAdd(v);
                      }}
                    >
                      {/* ✅ Packs are driven from decisionTopics.ts */}
                      <optgroup label={t("clientDecisions.topicPacksGroup")}>
                        {TOPIC_PACKS.map((p) => (
                          <option key={p.key} value={`${PACK_PREFIX}${p.key}`}>
                            {p.label}
                          </option>
                        ))}
                      </optgroup>

                      {topicGroupsList.map((g) => (
                        <optgroup key={g} label={g}>
                          {TOPICS.filter((t) => t.group === g).map((t) => (
                            <option key={`${g}__${t.value}`} value={t.value}>
                              {t.value}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>

                    {isPackValue(addTopic) ? (
                      <div style={{ marginTop: 6, fontSize: 12, color: "var(--muted)", fontWeight: 800 }}>
                        {t("clientDecisions.packSelectedNote")}
                      </div>
                    ) : null}
                  </div>

                  <div className="cdField">
                    <div className="cdLabel">{t("clientDecisions.statusLabel")}</div>
                    <select className="cdInput" value={addStatus} onChange={(e) => setAddStatus(e.target.value as DecisionStatus)}>
                      {DECISION_STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="cdField" style={{ gridColumn: "1 / -1" }}>
                    <div className="cdLabel">{t("clientDecisions.selectionLabel")}</div>
                    <input
                      className="cdInput"
                      value={addSelection}
                      onChange={(e) => setAddSelection(e.target.value)}
                      placeholder='e.g. 72" floating vanity, matte black faucet, 12x24 vertical stack...'
                      disabled={isPackValue(addTopic)}
                      title={isPackValue(addTopic) ? "Selection is auto-set to TBD for packs." : undefined}
                    />
                  </div>

                  <div className="cdField" style={{ gridColumn: "1 / -1" }}>
                    <div className="cdLabel">{t("clientDecisions.impactsLabel")}</div>
                    <div className="cdChecks">
                      <label className="cdCheck">
                        <input type="checkbox" checked={addImpactP} onChange={(e) => setAddImpactP(e.target.checked)} />
                        🔧 {t("clientDecisions.plumbingImpact")}
                      </label>
                      <label className="cdCheck">
                        <input type="checkbox" checked={addImpactE} onChange={(e) => setAddImpactE(e.target.checked)} />
                        ⚡ {t("clientDecisions.electricalImpact")}
                      </label>
                      <label className="cdCheck">
                        <input type="checkbox" checked={addImpactL} onChange={(e) => setAddImpactL(e.target.checked)} />
                        📐 {t("clientDecisions.layoutImpact")}
                      </label>
                    </div>
                    {isPackValue(addTopic) ? (
                      <div style={{ marginTop: 6, fontSize: 12, color: "var(--muted)", fontWeight: 800 }}>
                        {t("clientDecisions.packsDefaultImpactsNote")}
                      </div>
                    ) : null}
                  </div>

                  <div className="cdField">
                    <div className="cdLabel">{t("clientDecisions.impactNotesLabel")}</div>
                    <input
                      className="cdInput"
                      value={addImpactNotes}
                      onChange={(e) => setAddImpactNotes(e.target.value)}
                      placeholder="e.g. relocate trap, add new GFCI, move switch..."
                      disabled={isPackValue(addTopic)}
                    />
                  </div>

                  <div className="cdField">
                    <div className="cdLabel">{t("clientDecisions.locationNotesLabel")}</div>
                    <input
                      className="cdInput"
                      value={addLocationNotes}
                      onChange={(e) => setAddLocationNotes(e.target.value)}
                      placeholder='e.g. "left of vanity", "north wall", "centered on mirror"'
                      disabled={isPackValue(addTopic)}
                    />
                  </div>

                  <div className="cdField">
                    <div className="cdLabel">{t("clientDecisions.notesLabel")}</div>
                    <input
                      className="cdInput"
                      value={addNotes}
                      onChange={(e) => setAddNotes(e.target.value)}
                      placeholder={t("clientDecisions.notesPlaceholder")}
                      disabled={isPackValue(addTopic)}
                    />
                  </div>
                </div>
              </TableShell>
            </div>
          ) : null}
        </div>

        {/* Results */}
        {loading ? <div style={{ marginTop: 12, color: "var(--muted)", fontWeight: 900 }}>{t("clientDecisions.loadingMsg")}</div> : null}

        {viewMode === "board" ? (
          <div className="cdBoardGrid">
            {groupedForBoard.keys.length === 0 ? (
              <div style={{ color: "var(--muted)", fontStyle: "italic", padding: 8 }}>
                {t("clientDecisions.noDecisionsFiltered")}
              </div>
            ) : null}

            {groupedForBoard.keys.map((areaKey) => {
              const list = groupedForBoard.buckets[areaKey] || [];
              const areaName = areaKey === "__WHOLE__" ? t("clientDecisions.wholeProject") : areaNameById[areaKey] || "Area";

              const impacts = {
                p: list.filter((d) => d.impactPlumbing).length,
                e: list.filter((d) => d.impactElectrical).length,
                l: list.filter((d) => d.impactLayout).length,
                unconfirmed: list.filter((d) => d.status === "draft").length,
              };

              return (
                <div key={areaKey} className="cdAreaCard">
                  <div className="cdAreaHdr">
                    <div className="cdAreaTitle">
                      <span>{areaName}</span>
                      <button className="btn btnSm btnSecondary" type="button" onClick={() => printArea(areaKey)} title={t("clientDecisions.printAreaTip")}>
                        {t("clientDecisions.printBtn")}
                      </button>
                    </div>

                    <div className="cdAreaMeta">
                      <span>
                        {list.length} item{list.length === 1 ? "" : "s"}
                      </span>
                      <span>{t("clientDecisions.unconfirmedLabel")}: {impacts.unconfirmed}</span>
                      <span>
                        🔧{impacts.p} ⚡{impacts.e} 📐{impacts.l}
                      </span>
                    </div>
                  </div>

                  <div className="cdList">
                    {list.map((d) => {
                      const isEditing = editingId === d.id;

                      return (
                        <div key={d.id} className={`cdItem ${isEditing ? "editing" : ""}`}>
                          <div className="cdItemLeft">
                            <div className="cdItemTop">
                              <span className="cdTopic">{d.topic}</span>
                              <span className="cdStatus">{d.status}</span>
                              <span className="badge" title={t("clientDecisions.impactsTip")}>
                                {impactsText(d) || "—"}
                              </span>
                            </div>

                            {!isEditing ? (
                              <>
                                <CellClamp lines={2} title={d.selection}>
                                  <div className="cdSel">{d.selection}</div>
                                </CellClamp>

                                {d.locationNotes || d.impactNotes ? (
                                  <div className="cdMuted">
                                    {d.locationNotes ? `${t("clientDecisions.locationPrefix")} ${d.locationNotes}` : null}
                                    {d.locationNotes && d.impactNotes ? " • " : null}
                                    {d.impactNotes ? `${t("clientDecisions.impactPrefix")} ${d.impactNotes}` : null}
                                  </div>
                                ) : null}
                              </>
                            ) : (
                              <div className="cdInlineEditWrap">
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 10,
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                  }}
                                >
                                  <div style={{ fontWeight: 1000 }}>{t("clientDecisions.editDecisionTitle")}</div>
                                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                    <button className="btn btnSm" type="button" onClick={saveEdit}>
                                      {t("clientDecisions.saveBtn")}
                                    </button>
                                    <button className="btn btnSecondary btnSm" type="button" onClick={cancelEdit}>
                                      {t("clientDecisions.cancelBtn")}
                                    </button>
                                    <button
                                      className="btn btnSecondary btnSm"
                                      type="button"
                                      onClick={() => doDelete(d)}
                                      style={{ color: "#b00020" }}
                                      title={t("clientDecisions.deleteTip")}
                                    >
                                      {t("clientDecisions.deleteBtn")}
                                    </button>
                                  </div>
                                </div>

                                <div style={{ marginTop: 10 }} className="cdAddGrid">
                                  <div className="cdField">
                                    <div className="cdLabel">{t("clientDecisions.areaLabel")}</div>
                                    <select className="cdInput" value={editAreaId} onChange={(e) => setEditAreaId(e.target.value)}>
                                      <option value="">{t("clientDecisions.wholeProject")}</option>
                                      {areas.map((a) => (
                                        <option key={a.id} value={a.id}>
                                          {a.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  <div className="cdField">
                                    <div className="cdLabel">{t("clientDecisions.topicLabel")}</div>
                                    <select
                                      className="cdInput"
                                      value={editTopic}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setEditTopic(v);
                                        applyTopicDefaultsToEdit(v);
                                      }}
                                    >
                                      {topicGroupsList.map((g) => (
                                        <optgroup key={g} label={g}>
                                          {TOPICS.filter((t) => t.group === g).map((t) => (
                                            <option key={`${g}__${t.value}`} value={t.value}>
                                              {t.value}
                                            </option>
                                          ))}
                                        </optgroup>
                                      ))}
                                    </select>
                                  </div>

                                  <div className="cdField">
                                    <div className="cdLabel">{t("clientDecisions.statusLabel")}</div>
                                    <select className="cdInput" value={editStatus} onChange={(e) => setEditStatus(e.target.value as any)}>
                                      {DECISION_STATUSES.map((s) => (
                                        <option key={s.value} value={s.value}>
                                          {s.label}
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  <div className="cdField" style={{ gridColumn: "1 / -1" }}>
                                    <div className="cdLabel">{t("clientDecisions.selectionLabel")}</div>
                                    <input className="cdInput" value={editSelection} onChange={(e) => setEditSelection(e.target.value)} />
                                  </div>

                                  <div className="cdField" style={{ gridColumn: "1 / -1" }}>
                                    <div className="cdLabel">{t("clientDecisions.impactsLabel")}</div>
                                    <div className="cdChecks">
                                      <label className="cdCheck">
                                        <input type="checkbox" checked={editImpactP} onChange={(e) => setEditImpactP(e.target.checked)} />
                                        🔧 {t("clientDecisions.plumbingImpact")}
                                      </label>
                                      <label className="cdCheck">
                                        <input type="checkbox" checked={editImpactE} onChange={(e) => setEditImpactE(e.target.checked)} />
                                        ⚡ {t("clientDecisions.electricalImpact")}
                                      </label>
                                      <label className="cdCheck">
                                        <input type="checkbox" checked={editImpactL} onChange={(e) => setEditImpactL(e.target.checked)} />
                                        📐 {t("clientDecisions.layoutImpact")}
                                      </label>
                                    </div>
                                  </div>

                                  <div className="cdField">
                                    <div className="cdLabel">{t("clientDecisions.impactNotesLabel")}</div>
                                    <input className="cdInput" value={editImpactNotes} onChange={(e) => setEditImpactNotes(e.target.value)} />
                                  </div>

                                  <div className="cdField">
                                    <div className="cdLabel">{t("clientDecisions.locationNotesLabel")}</div>
                                    <input className="cdInput" value={editLocationNotes} onChange={(e) => setEditLocationNotes(e.target.value)} />
                                  </div>

                                  <div className="cdField">
                                    <div className="cdLabel">{t("clientDecisions.notesLabel")}</div>
                                    <input className="cdInput" value={editNotes} onChange={(e) => setEditNotes(e.target.value)} />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Right-side actions only when NOT editing (hidden via CSS when editing) */}
                          <div className="cdActions">
                            <button className="btn btnSm btnSecondary" type="button" onClick={() => startEdit(d)}>
                              {t("clientDecisions.editBtn")}
                            </button>
                            <button
                              className="btn btnSm btnSecondary"
                              type="button"
                              onClick={() => doDelete(d)}
                              style={{ color: "#b00020" }}
                              title={t("clientDecisions.deleteTip")}
                            >
                              {t("clientDecisions.deleteBtn")}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ marginTop: 14 }}>
            <TableShell
              title={t("clientDecisions.decisionLogTitle")}
              subtitle={filtered.length ? `${filtered.length} ${filtered.length === 1 ? t("clientDecisions.itemsSuffix") : t("clientDecisions.itemsSuffixPlural")}` : t("clientDecisions.noDecisions")}
              right={
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <span className="badge">
                    {t("clientDecisions.unconfirmedLabel")} <b>{filtered.filter((d) => d.status === "draft").length}</b>
                  </span>
                  <button className="btn btnSm btnSecondary" type="button" onClick={printTable}>
                    {t("clientDecisions.printBtn")}
                  </button>
                </div>
              }
            >
              {filtered.length === 0 ? (
                <div style={{ color: "var(--muted)", fontStyle: "italic", padding: "10px 2px" }}>
                    {t("clientDecisions.noDecisionsFiltered")}
                </div>
                ) : (
                <div className="cdTableWrap">
                    <table>
                    <thead>
                        <tr>
                        <th style={{ width: 220 }}>{t("clientDecisions.areaCol")}</th>
                        <th style={{ width: 260 }}>{t("clientDecisions.topicCol")}</th>
                        <th style={{ width: 420 }}>{t("clientDecisions.selectionCol")}</th>
                        <th style={{ width: 150 }}>{t("clientDecisions.statusCol")}</th>
                        <th style={{ width: 150 }}>{t("clientDecisions.impactsCol")}</th>
                        <th style={{ width: 220 }} />
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((d) => {
                        const areaName = d.areaId ? areaNameById[d.areaId] || t("clientDecisions.area") : t("clientDecisions.wholeProject");
                        const isEditing = editingId === d.id;

                        return (
                            <React.Fragment key={d.id}>
                            <tr>
                                <td>
                                <CellClamp lines={2} title={areaName}>
                                    <span style={{ fontWeight: 900 }}>{areaName}</span>
                                </CellClamp>
                                </td>

                                <td>
                                <CellClamp lines={2} title={d.topic}>
                                    <span style={{ fontWeight: 1000 }}>{d.topic}</span>
                                </CellClamp>
                                </td>

                                <td>
                                <CellClamp lines={2} title={d.selection}>
                                    {d.selection}
                                </CellClamp>
                                {d.locationNotes || d.impactNotes ? (
                                    <div style={{ marginTop: 6, fontSize: 12, color: "var(--muted)", fontWeight: 800 }}>
                                    {d.locationNotes ? `${t("clientDecisions.locationPrefix")} ${d.locationNotes}` : null}
                                    {d.locationNotes && d.impactNotes ? " • " : null}
                                    {d.impactNotes ? `${t("clientDecisions.impactPrefix")} ${d.impactNotes}` : null}
                                    </div>
                                ) : null}
                                </td>

                                <td>
                                <span className="badge">{d.status}</span>
                                </td>

                                <td>
                                <span className="badge">{impactsText(d) || "—"}</span>
                                </td>

                                <td>
                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                                    <button
                                    className="btn btnSm btnSecondary"
                                    type="button"
                                    onClick={() => (isEditing ? cancelEdit() : startEdit(d))}
                                    >
                                    {isEditing ? t("clientDecisions.closeBtn") : t("clientDecisions.editBtn")}
                                    </button>
                                    <button
                                    className="btn btnSm btnSecondary"
                                    type="button"
                                    onClick={() => doDelete(d)}
                                    style={{ color: "#b00020" }}
                                    disabled={isEditing}
                                    title={isEditing ? t("clientDecisions.closeEditToDelete") : t("clientDecisions.deleteBtn")}
                                    >
                                    {t("clientDecisions.deleteBtn")}
                                    </button>
                                </div>
                                </td>
                            </tr>

                            {isEditing ? (
                                <tr>
                                <td colSpan={6}>
                                    <div className="cdInlineEditWrap">
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                                        <div style={{ fontWeight: 1000 }}>{t("clientDecisions.editDecisionTitle")}</div>
                                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                        <button className="btn btnSm" type="button" onClick={saveEdit}>
                                            {t("clientDecisions.saveBtn")}
                                        </button>
                                        <button className="btn btnSecondary btnSm" type="button" onClick={cancelEdit}>
                                            {t("clientDecisions.cancelBtn")}
                                        </button>
                                        </div>
                                    </div>

                                    <div style={{ marginTop: 10 }} className="cdAddGrid">
                                        <div className="cdField">
                                        <div className="cdLabel">{t("clientDecisions.areaLabel")}</div>
                                        <select className="cdInput" value={editAreaId} onChange={(e) => setEditAreaId(e.target.value)}>
                                            <option value="">{t("clientDecisions.wholeProject")}</option>
                                            {areas.map((a) => (
                                            <option key={a.id} value={a.id}>
                                                {a.name}
                                            </option>
                                            ))}
                                        </select>
                                        </div>

                                        <div className="cdField">
                                        <div className="cdLabel">{t("clientDecisions.topicLabel")}</div>
                                        <select
                                            className="cdInput"
                                            value={editTopic}
                                            onChange={(e) => {
                                            const v = e.target.value;
                                            setEditTopic(v);
                                            applyTopicDefaultsToEdit(v);
                                            }}
                                        >
                                            {topicGroupsList.map((g) => (
                                            <optgroup key={g} label={g}>
                                                {TOPICS.filter((t) => t.group === g).map((t) => (
                                                <option key={`${g}__${t.value}`} value={t.value}>
                                                    {t.value}
                                                </option>
                                                ))}
                                            </optgroup>
                                            ))}
                                        </select>
                                        </div>

                                        <div className="cdField">
                                        <div className="cdLabel">{t("clientDecisions.statusLabel")}</div>
                                        <select className="cdInput" value={editStatus} onChange={(e) => setEditStatus(e.target.value as any)}>
                                            {DECISION_STATUSES.map((s) => (
                                            <option key={s.value} value={s.value}>
                                                {s.label}
                                            </option>
                                            ))}
                                        </select>
                                        </div>

                                        <div className="cdField" style={{ gridColumn: "1 / -1" }}>
                                        <div className="cdLabel">{t("clientDecisions.selectionLabel")}</div>
                                        <input className="cdInput" value={editSelection} onChange={(e) => setEditSelection(e.target.value)} />
                                        </div>

                                        <div className="cdField" style={{ gridColumn: "1 / -1" }}>
                                        <div className="cdLabel">{t("clientDecisions.impactsLabel")}</div>
                                        <div className="cdChecks">
                                            <label className="cdCheck">
                                            <input type="checkbox" checked={editImpactP} onChange={(e) => setEditImpactP(e.target.checked)} />
                                            🔧 {t("clientDecisions.plumbingLabel")}
                                            </label>
                                            <label className="cdCheck">
                                            <input type="checkbox" checked={editImpactE} onChange={(e) => setEditImpactE(e.target.checked)} />
                                            ⚡ {t("clientDecisions.electricalLabel")}
                                            </label>
                                            <label className="cdCheck">
                                            <input type="checkbox" checked={editImpactL} onChange={(e) => setEditImpactL(e.target.checked)} />
                                            📐 {t("clientDecisions.layoutLabel")}
                                            </label>
                                        </div>
                                        </div>

                                        <div className="cdField">
                                        <div className="cdLabel">{t("clientDecisions.impactNotesLabel")}</div>
                                        <input className="cdInput" value={editImpactNotes} onChange={(e) => setEditImpactNotes(e.target.value)} />
                                        </div>

                                        <div className="cdField">
                                        <div className="cdLabel">{t("clientDecisions.locationNotesLabel")}</div>
                                        <input className="cdInput" value={editLocationNotes} onChange={(e) => setEditLocationNotes(e.target.value)} />
                                        </div>

                                        <div className="cdField">
                                        <div className="cdLabel">{t("clientDecisions.notesLabel")}</div>
                                        <input className="cdInput" value={editNotes} onChange={(e) => setEditNotes(e.target.value)} />
                                        </div>
                                    </div>
                                    </div>
                                </td>
                                </tr>
                            ) : null}
                            </React.Fragment>
                        );
                        })}
                    </tbody>
                    </table>
                </div>
                )}
            </TableShell>
          </div>
        )}
      </Card>
    </div>
  );
}
